import { useMemo } from "react";
import { Button } from "@deepguide-ai/ui/button";
import { useIntegrationConnectContext } from "@/components/screenata/integrations/IntegrationConnectContext";
import { canOpenConnect } from "@/components/screenata/integrations/useIntegrationConnect";
import { IntegrationIcon } from "@/pages/integrations/providers/icons";
import type { IntegrationType } from "@deepguide-ai/types";

interface IntegrationConnectAction {
  type: "integration.connect";
  integrationType: string;
  label?: string;
  reason?: string;
}

interface IntegrationConnectMessageProps {
  /** Tool part output may contain a `connectAction` (single) or
   * `connectActions` (array — e.g. integrations.list returns one per
   * disconnected integration). */
  part: {
    output?: unknown;
    state?: string;
    toolInvocation?: { result?: string };
  };
}

function isConnectAction(value: unknown): value is IntegrationConnectAction {
  return (
    !!value &&
    typeof value === "object" &&
    (value as { type?: unknown }).type === "integration.connect" &&
    typeof (value as { integrationType?: unknown }).integrationType === "string"
  );
}

function parsePayload(part: IntegrationConnectMessageProps["part"]): IntegrationConnectAction[] {
  const raw =
    part.state === "output-available"
      ? part.output
      : part.toolInvocation?.result;
  if (!raw) return [];

  let data: Record<string, unknown>;
  if (typeof raw === "string") {
    // Cheap pre-flight: skip the JSON.parse on outputs that can't possibly
    // carry a connect action. Tool messages render frequently, and most
    // outputs (population dumps, vault scans) are large blobs without one.
    if (!raw.includes('"integration.connect"')) return [];
    try {
      data = JSON.parse(raw);
    } catch {
      return [];
    }
  } else if (typeof raw === "object") {
    data = raw as Record<string, unknown>;
  } else {
    return [];
  }

  const actions: IntegrationConnectAction[] = [];
  if (Array.isArray(data.connectActions)) {
    for (const candidate of data.connectActions) {
      if (isConnectAction(candidate) && canOpenConnect(candidate.integrationType)) {
        actions.push(candidate);
      }
    }
  }
  if (
    isConnectAction(data.connectAction) &&
    canOpenConnect(data.connectAction.integrationType)
  ) {
    actions.push(data.connectAction);
  }
  return actions;
}

export function IntegrationConnectMessage({ part }: IntegrationConnectMessageProps) {
  const actions = useMemo(() => parsePayload(part), [part]);
  const ctx = useIntegrationConnectContext();

  if (actions.length === 0) return null;
  // No provider mounted on this surface (e.g. an onboarding route outside
  // /screenata) — silently skip rather than crash.
  if (!ctx) return null;
  const { openConnect } = ctx;

  // Intentionally no busy state. The connect dialog is modal/popup and
  // blocks further input on its own; an "Opening…" label here adds no
  // signal and gets stuck whenever a provider's handleConnect catches
  // its own error without rejecting (e.g. Microsoft Teams missing OAuth
  // creds — toast appears, label stays).
  return (
    <div className="flex flex-col gap-2 my-1">
      {actions.map((action) => (
        <Button
          key={action.integrationType}
          type="button"
          variant="outline"
          className="w-full justify-start h-11 text-sm"
          onClick={() => openConnect(action.integrationType)}
          title={action.reason ?? undefined}
        >
          <span className="mr-2.5 flex h-6 w-6 shrink-0 items-center justify-center [&>svg]:h-full [&>svg]:w-full">
            <IntegrationIcon type={action.integrationType as IntegrationType} />
          </span>
          {action.label ?? `Connect ${action.integrationType}`}
        </Button>
      ))}
    </div>
  );
}
